import { Injectable, computed, inject, signal, type Signal } from '@angular/core';

import { ContentFacade } from '@web/store/website/content/content.facade';
import { LayoutFacade } from '@web/store/layout/layout.facade';

/**
 * Whether an AI reply is in flight — the flag the canvas blocker reads (§7 of `ai-mcp-plan.md`,
 * "блокировка конструктора"), and the owner of the autosave pause that has to travel with it.
 *
 * **Why the canvas has to be blocked at all.** A reply's tool calls are executed server-side and
 * mirrored into the NgRx stores step by step, then the whole page is re-read once the reply ends
 * (§4.3 variant A). That re-read takes the SERVER's copy, so anything the user typed on the canvas
 * meanwhile is silently overwritten — and the user has no way to know a background reply is the
 * reason their edit vanished. Blocking is the honest answer until the save-time version check
 * (§2.1, with the backend) makes the two directions safe to interleave.
 *
 * **Why the autosave is paused for the same window.** The editor autosaves 3s after any structural
 * action (`content.effects.ts`), and the mirror above dispatches exactly those actions —
 * `widgetCreated`, `columnCreated`, `updateBlockProperties`, `updateColumnProperties` are all on
 * that trigger list. So without a pause every AI mutation ARMS the editor's autosave, which then
 * POSTs the browser's whole structure over what `apps/mcp-backend` just wrote. On a page where the
 * mirror got any field slightly wrong, that silently reverts the server to the browser's belief —
 * a lost update by construction, mid-reply, before the end-of-reply re-read can settle anything.
 *
 * The pause mechanism is not new: `setAutoSavePaused` already exists and is what a zero-block drag
 * uses to keep the autosave out of an interaction it would corrupt. An AI reply is the same class
 * of event, only longer.
 *
 * Order matters on the way out and is enforced by the caller (`SendpulseChatTransport`'s `finally`):
 * re-read the page, THEN `end()`. Unpausing before the re-read hands the still-stale browser
 * structure back to the autosave — exactly the write the pause existed to prevent.
 *
 * A signal service rather than NgRx: this is transient view state with no reducer, no history and
 * nothing to persist — the same call the codebase already made for the navigation selection. It also
 * has to be readable synchronously from a plain `ChatTransport` that lives outside the store.
 *
 * Deliberately NOT owned by `McpToolsService`: the canvas would then depend on the whole MCP tool
 * layer (which dynamically imports the MCP SDK) to answer one boolean.
 */
@Injectable({ providedIn: 'root' })
export class AiSessionService {
    private readonly contentFacade = inject(ContentFacade);
    private readonly layoutFacade = inject(LayoutFacade);

    private readonly active = signal(false);

    /** True from the moment a reply starts until its end-of-reply page re-read has finished. */
    public readonly isActive: Signal<boolean> = computed(() => this.active());

    public begin(): void {
        this.active.set(true);
        this.contentFacade.setAutoSavePaused(true);
        // §7 "preview mode": hides hover outlines/+add-buttons/drag-handles for the same window
        // (`LayoutSelectors.selectIsCanvasPreviewActive`), and closes whatever settings panel was
        // open — it would otherwise keep floating over a canvas the user can no longer edit.
        this.layoutFacade.setAiReplyActive(true);
        this.layoutFacade.hideSelectedEntitySettings();
    }

    /**
     * Ends the session. Must be reached on EVERY exit path of a reply — including an abort, a
     * network failure and a re-read that timed out — or the canvas stays blocked and the autosave
     * stays paused with no way back short of a reload. Its caller keeps it in a `finally` for that
     * reason, and a paused autosave is the more dangerous of the two leaks: the canvas at least
     * LOOKS broken, whereas a silently unsaving editor looks perfectly fine.
     */
    public end(): void {
        this.contentFacade.setAutoSavePaused(false);
        this.layoutFacade.setAiReplyActive(false);
        this.active.set(false);
    }
}
